import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { safeParseJson } from '../utils/auth';

const CANADIAN_PROVINCES = [
  { code: 'ON', name: 'Ontario (HST 13%)' },
  { code: 'BC', name: 'British Columbia (GST+PST 12%)' },
  { code: 'AB', name: 'Alberta (GST 5%)' },
  { code: 'QC', name: 'Quebec (GST+QST 14.975%)' },
  { code: 'NS', name: 'Nova Scotia (HST 15%)' },
  { code: 'NB', name: 'New Brunswick (HST 15%)' },
  { code: 'NL', name: 'Newfoundland & Labrador (HST 15%)' },
  { code: 'PE', name: 'Prince Edward Island (HST 15%)' },
  { code: 'MB', name: 'Manitoba (GST+RST 12%)' },
  { code: 'SK', name: 'Saskatchewan (GST+PST 11%)' },
  { code: 'NT', name: 'Northwest Territories (GST 5%)' },
  { code: 'NU', name: 'Nunavut (GST 5%)' },
  { code: 'YT', name: 'Yukon (GST 5%)' }
];

const LOCAL_ORDERS_KEY = 'best3_local_orders_v1';

export const saveLocalOrderSnapshot = (orderObj) => {
  if (!orderObj || !orderObj.order_number) return;
  try {
    const existing = JSON.parse(localStorage.getItem(LOCAL_ORDERS_KEY) || '[]');
    const filtered = Array.isArray(existing)
      ? existing.filter((o) => o.order_number !== orderObj.order_number)
      : [];
    filtered.unshift(orderObj);
    localStorage.setItem(LOCAL_ORDERS_KEY, JSON.stringify(filtered.slice(0, 50)));
  } catch (_e) {}
};

const CheckoutPage = () => {
  const {
    items,
    clearCart,
    settings,
    subtotal,
    discountAmount,
    discountSummary,
    freeShippingUnlocked,
    freeShippingReason,
    promoCodeInput,
    setPromoCodeInput,
    appliedPromoRule,
    promoFeedback,
    applyPromoCode,
    removePromoCode,
    consumedDiscountIds,
    recordLocalDiscountRedemptions,
    requiresShipping,
    shippingAmount,
    selectedCountry,
    setSelectedCountry,
    selectedRegion,
    setSelectedRegion,
    activeTaxZone,
    taxAmount,
    totalAmount,
    currency,
    formatPrice
  } = useCart();

  const navigate = useNavigate();

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerNotes, setCustomerNotes] = useState('');

  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [postalCode, setPostalCode] = useState('');

  const [formError, setFormError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  // PayPal Approval Modal State
  const [pendingPayPalOrder, setPendingPayPalOrder] = useState(null);
  const [paypalPayerEmail, setPaypalPayerEmail] = useState('');
  const [confirmedOrder, setConfirmedOrder] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    setIsLoggedIn(!!token);
    try {
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      const fullName = `${storedUser.firstName || storedUser.first_name || ''} ${
        storedUser.lastName || storedUser.last_name || ''
      }`.trim();
      if (fullName) setCustomerName((prev) => prev || fullName);
      if (storedUser.email) {
        setCustomerEmail((prev) => prev || storedUser.email);
        setPaypalPayerEmail((prev) => prev || storedUser.email);
      }
    } catch (_e) {}
  }, []);

  const validateCheckoutForm = () => {
    setFormError('');
    if (!customerName.trim()) {
      setFormError('Please enter your full name.');
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(customerEmail.trim())) {
      setFormError('Please enter a valid email address for your order receipt.');
      return false;
    }
    if (requiresShipping) {
      if (!addressLine1.trim() || !city.trim() || !selectedRegion.trim() || !postalCode.trim()) {
        setFormError('Please fill in all required shipping address fields.');
        return false;
      }
    }
    return true;
  };

  const handlePromoSubmit = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    applyPromoCode(promoCodeInput);
  };

  const handleInitiatePayPalCheckout = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!validateCheckoutForm()) return;

    setIsProcessing(true);
    setFormError('');

    const token = localStorage.getItem('token');
    const headers = { 'Content-Type': 'application/json' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const activePromoCode = appliedPromoRule?.code || '';

    const payload = {
      currency,
      promo_code: activePromoCode,
      customer: {
        name: customerName.trim(),
        email: customerEmail.trim(),
        phone: customerPhone.trim(),
        notes: customerNotes.trim()
      },
      shipping_address: {
        addressLine1: addressLine1.trim(),
        addressLine2: addressLine2.trim(),
        city: city.trim(),
        state: selectedRegion.trim(),
        postalCode: postalCode.trim(),
        country: selectedCountry.trim()
      },
      items: items.map((item) => ({
        product_id: item.productId,
        quantity: item.quantity,
        variant: item.variant || ''
      }))
    };

    try {
      const res = await fetch('/best3intennis-api/api/paypal_create_order.php', {
        method: 'POST',
        headers,
        body: JSON.stringify(payload)
      });

      const text = await res.text();
      const data = safeParseJson(text);

      if (res.ok && data && data.success) {
        if (recordLocalDiscountRedemptions && Array.isArray(consumedDiscountIds)) {
          recordLocalDiscountRedemptions(consumedDiscountIds);
        }
        setPaypalPayerEmail(customerEmail.trim());
        setPendingPayPalOrder({
          order_number: data.order_number,
          paypal_order_id: data.paypal_order_id,
          breakdown: data.breakdown || {
            subtotal,
            discount_amount: discountAmount,
            discount_code: activePromoCode,
            discount_summary: discountSummary,
            shipping: shippingAmount,
            tax_name: activeTaxZone?.tax_name || 'HST',
            tax_rate: activeTaxZone?.tax_rate ?? 13,
            tax_amount: taxAmount,
            total: totalAmount,
            currency
          }
        });
        setIsProcessing(false);
        return;
      }

      if (data && data.message && res.status >= 400 && res.status < 500 && res.status !== 404) {
        setFormError(data.message);
        setIsProcessing(false);
        return;
      }
    } catch (_netErr) {
      // Fallback for local pre-FTP testing when PHP endpoint is not yet live
    }

    // Resilient fallback for local pre-upload environment
    const fallbackOrderNum =
      'B3T-' +
      new Date().toISOString().slice(0, 10).replace(/-/g, '') +
      '-' +
      Math.random().toString(36).substring(2, 7).toUpperCase();
    const fallbackPaypalId = 'PAYPAL-ORD-' + Math.random().toString(36).substring(2, 10).toUpperCase();

    if (recordLocalDiscountRedemptions && Array.isArray(consumedDiscountIds)) {
      recordLocalDiscountRedemptions(consumedDiscountIds);
    }

    setPaypalPayerEmail(customerEmail.trim());
    setPendingPayPalOrder({
      order_number: fallbackOrderNum,
      paypal_order_id: fallbackPaypalId,
      is_local_fallback: true,
      breakdown: {
        subtotal,
        discount_amount: discountAmount,
        discount_code: activePromoCode,
        discount_summary: discountSummary,
        shipping: shippingAmount,
        tax_name: activeTaxZone?.tax_name || 'HST',
        tax_rate: activeTaxZone?.tax_rate ?? 13,
        tax_amount: taxAmount,
        total: totalAmount,
        currency
      }
    });
    setIsProcessing(false);
  };

  const handleConfirmPayPalCapture = async () => {
    if (!pendingPayPalOrder) return;
    setIsProcessing(true);
    setFormError('');

    const token = localStorage.getItem('token');
    const headers = { 'Content-Type': 'application/json' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    try {
      const res = await fetch('/best3intennis-api/api/paypal_capture_order.php', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          order_number: pendingPayPalOrder.order_number,
          paypal_order_id: pendingPayPalOrder.paypal_order_id,
          payer_email: paypalPayerEmail || customerEmail.trim()
        })
      });

      const text = await res.text();
      const data = safeParseJson(text);

      if (res.ok && data && data.success && data.order) {
        saveLocalOrderSnapshot(data.order);
        setConfirmedOrder(data.order);
        setPendingPayPalOrder(null);
        clearCart();
        setIsProcessing(false);
        return;
      }
    } catch (_err) {
      // Local pre-upload fallback
    }

    const simulatedCapturedOrder = {
      order_number: pendingPayPalOrder.order_number,
      customer_name: customerName.trim(),
      customer_email: customerEmail.trim(),
      customer_phone: customerPhone.trim(),
      requires_shipping: requiresShipping,
      shipping_address: requiresShipping
        ? {
            addressLine1: addressLine1.trim(),
            addressLine2: addressLine2.trim(),
            city: city.trim(),
            state: selectedRegion.trim(),
            postalCode: postalCode.trim(),
            country: selectedCountry.trim()
          }
        : { country: selectedCountry, state: selectedRegion, note: 'Digital delivery' },
      subtotal_amount: pendingPayPalOrder.breakdown.subtotal,
      discount_amount: pendingPayPalOrder.breakdown.discount_amount || 0,
      discount_code: pendingPayPalOrder.breakdown.discount_code || '',
      discount_summary: pendingPayPalOrder.breakdown.discount_summary || '',
      shipping_amount: pendingPayPalOrder.breakdown.shipping,
      tax_name: pendingPayPalOrder.breakdown.tax_name,
      tax_rate: pendingPayPalOrder.breakdown.tax_rate,
      tax_amount: pendingPayPalOrder.breakdown.tax_amount,
      total_amount: pendingPayPalOrder.breakdown.total,
      currency: pendingPayPalOrder.breakdown.currency || 'CAD',
      payment_provider: 'paypal',
      paypal_order_id: pendingPayPalOrder.paypal_order_id,
      paypal_capture_id: 'CAP-' + Math.random().toString(36).substring(2, 10).toUpperCase(),
      paypal_payer_email: paypalPayerEmail || customerEmail.trim(),
      payment_status: 'paid',
      fulfillment_status: requiresShipping ? 'processing' : 'delivered',
      items: items.map((i) => ({
        product_id: i.productId,
        product_name: i.name,
        sku: i.sku,
        variant_selection: i.variant,
        unit_price: i.price,
        quantity: i.quantity,
        line_total: Number((i.price * i.quantity).toFixed(2)),
        product_type: i.productType,
        image_url: i.image
      })),
      created_at: new Date().toISOString()
    };

    saveLocalOrderSnapshot(simulatedCapturedOrder);
    setConfirmedOrder(simulatedCapturedOrder);
    setPendingPayPalOrder(null);
    clearCart();
    setIsProcessing(false);
  };

  // Order Confirmation View
  if (confirmedOrder) {
    return (
      <div className="min-h-screen py-12 px-4 bg-gradient-to-b from-green-50 to-white" data-testid="order-confirmation-view">
        <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-xl border border-green-200 overflow-hidden">
          <div className="bg-gradient-to-r from-green-700 to-green-600 text-white p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-white text-green-700 flex items-center justify-center text-3xl mx-auto mb-4 shadow-md font-extrabold">
              ✓
            </div>
            <h1 className="text-3xl font-extrabold mb-1">Thank You for Your Order!</h1>
            <p className="text-green-100 text-sm">
              Your PayPal payment has been verified and a receipt has been sent to{' '}
              <strong>{confirmedOrder.customer_email}</strong>.
            </p>
          </div>

          <div className="p-6 sm:p-8 space-y-6">
            <div className="bg-green-50/80 border border-green-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <div className="text-xs uppercase font-bold text-gray-500">Order Number</div>
                <div className="text-xl font-extrabold text-green-900" data-testid="confirmed-order-number">
                  {confirmedOrder.order_number}
                </div>
              </div>
              <div className="sm:text-right">
                <div className="text-xs uppercase font-bold text-gray-500">PayPal Transaction ID</div>
                <div className="text-sm font-mono font-semibold text-gray-800">
                  {confirmedOrder.paypal_capture_id || confirmedOrder.paypal_order_id}
                </div>
              </div>
            </div>

            {/* Itemized Receipt */}
            <div>
              <h2 className="text-sm font-extrabold uppercase tracking-wider text-gray-500 mb-3">
                Order Summary
              </h2>
              <div className="divide-y divide-gray-100 border border-gray-200 rounded-xl overflow-hidden">
                {(confirmedOrder.items || []).map((item, idx) => (
                  <div key={idx} className="p-3.5 flex items-center justify-between bg-white text-sm">
                    <div>
                      <span className="font-bold text-gray-900">{item.product_name}</span>
                      {item.variant_selection && (
                        <span className="ml-1.5 text-xs text-gray-500">({item.variant_selection})</span>
                      )}
                      <span className="ml-2 text-xs font-semibold text-gray-500">× {item.quantity}</span>
                    </div>
                    <div className="font-bold text-gray-900">
                      ${Number(item.line_total || 0).toFixed(2)} {confirmedOrder.currency}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Totals Breakdown */}
            <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-sm border border-gray-200">
              <div className="flex justify-between text-gray-600">
                <span>Subtotal</span>
                <span>
                  ${Number(confirmedOrder.subtotal_amount).toFixed(2)} {confirmedOrder.currency}
                </span>
              </div>
              {Number(confirmedOrder.discount_amount || 0) > 0 && (
                <div className="flex justify-between text-emerald-700 font-semibold" data-testid="confirmed-order-discount">
                  <span>
                    Discount {confirmedOrder.discount_summary ? `(${confirmedOrder.discount_summary})` : ''}
                  </span>
                  <span>
                    -${Number(confirmedOrder.discount_amount).toFixed(2)} {confirmedOrder.currency}
                  </span>
                </div>
              )}
              <div className="flex justify-between text-gray-600">
                <span>Shipping</span>
                <span>
                  {Number(confirmedOrder.shipping_amount) === 0
                    ? 'FREE'
                    : `$${Number(confirmedOrder.shipping_amount).toFixed(2)} ${confirmedOrder.currency}`}
                </span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span>
                  {confirmedOrder.tax_name || 'HST'} ({confirmedOrder.tax_rate ?? 13}%)
                </span>
                <span>
                  ${Number(confirmedOrder.tax_amount).toFixed(2)} {confirmedOrder.currency}
                </span>
              </div>
              <div className="flex justify-between text-base font-extrabold text-green-900 pt-2 border-t border-gray-200">
                <span>Total Paid via PayPal</span>
                <span data-testid="confirmed-order-total">
                  ${Number(confirmedOrder.total_amount).toFixed(2)} {confirmedOrder.currency}
                </span>
              </div>
            </div>

            <div className="p-3.5 bg-green-50 rounded-xl border border-green-200 text-xs text-green-900 text-center">
              <span>Have questions about your order or need support? Contact us at </span>
              <a
                href={`mailto:${settings?.paypal_support_email || 'support@best3intennis.com'}`}
                className="font-bold underline text-green-800 hover:text-green-950"
              >
                {settings?.paypal_support_email || 'support@best3intennis.com'}
              </a>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button
                type="button"
                onClick={() =>
                  navigate(
                    `/track-order?order=${encodeURIComponent(confirmedOrder.order_number)}&email=${encodeURIComponent(
                      confirmedOrder.customer_email
                    )}`
                  )
                }
                data-testid="track-confirmed-order-btn"
                className="flex-1 bg-green-700 hover:bg-green-800 text-white font-bold py-3 px-5 rounded-xl text-center shadow-sm transition cursor-pointer"
              >
                📦 Track Order Status
              </button>
              <NavLink
                to="/extras-shop"
                className="flex-1 bg-yellow-400 hover:bg-yellow-500 text-green-950 font-bold py-3 px-5 rounded-xl text-center shadow-sm transition"
              >
                Continue Shopping
              </NavLink>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Empty cart guard
  if (items.length === 0) {
    return (
      <div className="min-h-screen py-16 px-4 text-center">
        <div className="max-w-md mx-auto bg-white rounded-2xl shadow-md border border-gray-200 p-8">
          <div className="text-5xl mb-4">🛒</div>
          <h1 className="text-2xl font-extrabold text-green-900 mb-2">Your Cart is Empty</h1>
          <p className="text-sm text-gray-600 mb-6">
            Add products from the Best3inTennis Shop before proceeding to checkout.
          </p>
          <NavLink
            to="/extras-shop"
            className="inline-block bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-6 rounded-xl shadow-sm transition"
          >
            Return to Shop
          </NavLink>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-10 px-4 bg-gradient-to-b from-green-50/60 to-white" data-testid="checkout-page">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <NavLink to="/extras-shop" className="text-xs font-bold text-green-700 hover:underline">
              ← Back to Shop
            </NavLink>
            <h1 className="text-3xl font-extrabold text-green-900 mt-1">Secure Checkout</h1>
          </div>
          <div className="text-xs text-gray-600 bg-white px-3.5 py-2 rounded-xl border border-gray-200 shadow-2xs">
            Currency: <strong>{currency}</strong> • Tax Zone:{' '}
            <strong>
              {activeTaxZone?.tax_name || 'HST'} ({activeTaxZone?.tax_rate ?? 13}%)
            </strong>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Left Column: Customer & Shipping Form */}
          <form onSubmit={handleInitiatePayPalCheckout} className="lg:col-span-7 space-y-6">
            {/* Account Status Banner */}
            <div className="bg-white rounded-2xl p-5 shadow-xs border border-gray-200 flex items-center justify-between">
              {isLoggedIn ? (
                <div className="flex items-center gap-3 text-sm text-green-900">
                  <span className="text-xl">✅</span>
                  <div>
                    <p className="font-bold">Signed in to Best3inTennis</p>
                    <p className="text-xs text-gray-600">
                      This order will be automatically linked to your player account ({customerEmail}).
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col sm:flex-row sm:items-center justify-between w-full gap-2">
                  <div>
                    <p className="text-sm font-bold text-gray-900">Guest Checkout Enabled</p>
                    <p className="text-xs text-gray-500">
                      No account required—or sign in to link this order to your profile.
                    </p>
                  </div>
                  <NavLink
                    to="/login"
                    className="text-xs font-bold text-green-700 hover:text-green-800 underline whitespace-nowrap"
                  >
                    Have an account? Sign In
                  </NavLink>
                </div>
              )}
            </div>

            {formError && (
              <div
                className="bg-red-50 border-l-4 border-red-600 text-red-800 p-4 rounded-xl text-sm font-medium"
                role="alert"
                data-testid="checkout-form-error"
              >
                {formError}
              </div>
            )}

            {/* Contact Information */}
            <div className="bg-white rounded-2xl p-6 shadow-xs border border-gray-200 space-y-4">
              <h2 className="text-lg font-extrabold text-green-900 border-b border-gray-100 pb-2">
                1. Contact Information
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">Full Name *</label>
                  <input
                    type="text"
                    required
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    placeholder="Alex Morgan"
                    data-testid="checkout-name-input"
                    className="w-full px-3.5 py-2.5 rounded-xl border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-green-600"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">Email Address (for Receipt) *</label>
                  <input
                    type="email"
                    required
                    value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)}
                    placeholder="player@example.com"
                    data-testid="checkout-email-input"
                    className="w-full px-3.5 py-2.5 rounded-xl border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-green-600"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">Phone Number (Optional)</label>
                <input
                  type="tel"
                  value={customerPhone}
                  onChange={(e) => setCustomerPhone(e.target.value)}
                  placeholder="(416) 555-0199"
                  data-testid="checkout-phone-input"
                  className="w-full px-3.5 py-2.5 rounded-xl border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-green-600"
                />
              </div>
            </div>

            {/* Shipping & Tax Zone Location */}
            <div className="bg-white rounded-2xl p-6 shadow-xs border border-gray-200 space-y-4">
              <h2 className="text-lg font-extrabold text-green-900 border-b border-gray-100 pb-2">
                2. {requiresShipping ? 'Shipping Address & Tax Zone' : 'Billing Region & Tax Zone'}
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">Country *</label>
                  <select
                    value={selectedCountry}
                    onChange={(e) => {
                      const nextCountry = e.target.value;
                      setSelectedCountry(nextCountry);
                      if (nextCountry === 'CA') setSelectedRegion('ON');
                      else if (nextCountry === 'US') setSelectedRegion('NY');
                      else setSelectedRegion('*');
                    }}
                    data-testid="checkout-country-select"
                    className="w-full px-3.5 py-2.5 rounded-xl border border-gray-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-green-600"
                  >
                    <option value="CA">Canada (Default)</option>
                    <option value="US">United States</option>
                    <option value="INTL">International</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    Province / State (Determines Tax Zone) *
                  </label>
                  {selectedCountry === 'CA' ? (
                    <select
                      value={selectedRegion}
                      onChange={(e) => setSelectedRegion(e.target.value)}
                      data-testid="checkout-region-select"
                      className="w-full px-3.5 py-2.5 rounded-xl border border-gray-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-green-600"
                    >
                      {CANADIAN_PROVINCES.map((prov) => (
                        <option key={prov.code} value={prov.code}>
                          {prov.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      required
                      value={selectedRegion}
                      onChange={(e) => setSelectedRegion(e.target.value)}
                      placeholder="State / Province"
                      data-testid="checkout-region-input"
                      className="w-full px-3.5 py-2.5 rounded-xl border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-green-600"
                    />
                  )}
                </div>
              </div>

              {requiresShipping && (
                <>
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1">Street Address *</label>
                    <input
                      type="text"
                      required
                      value={addressLine1}
                      onChange={(e) => setAddressLine1(e.target.value)}
                      placeholder="123 Centre Court Way"
                      data-testid="checkout-address1-input"
                      className="w-full px-3.5 py-2.5 rounded-xl border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-green-600"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1">
                      Apartment, Suite, Club (Optional)
                    </label>
                    <input
                      type="text"
                      value={addressLine2}
                      onChange={(e) => setAddressLine2(e.target.value)}
                      placeholder="Suite 200"
                      className="w-full px-3.5 py-2.5 rounded-xl border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-green-600"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1">City *</label>
                      <input
                        type="text"
                        required
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        placeholder="Toronto"
                        data-testid="checkout-city-input"
                        className="w-full px-3.5 py-2.5 rounded-xl border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-green-600"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1">Postal / ZIP Code *</label>
                      <input
                        type="text"
                        required
                        value={postalCode}
                        onChange={(e) => setPostalCode(e.target.value)}
                        placeholder="M5V 2T6"
                        data-testid="checkout-postal-input"
                        className="w-full px-3.5 py-2.5 rounded-xl border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-green-600"
                      />
                    </div>
                  </div>
                </>
              )}

              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">
                  Order Notes / Gift Message (Optional)
                </label>
                <textarea
                  rows={2}
                  value={customerNotes}
                  onChange={(e) => setCustomerNotes(e.target.value)}
                  placeholder="Any special delivery instructions or gift card message..."
                  className="w-full px-3.5 py-2 rounded-xl border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-green-600"
                />
              </div>
            </div>
          </form>

          {/* Right Column: Order Summary & PayPal Payment Box */}
          <div className="lg:col-span-5 space-y-6">
            <div className="bg-white rounded-2xl p-6 shadow-md border border-gray-200 sticky top-20">
              <h2 className="text-lg font-extrabold text-green-900 border-b border-gray-100 pb-3 mb-4">
                Order Summary ({items.reduce((acc, i) => acc + i.quantity, 0)} items)
              </h2>

              <div className="space-y-3 max-h-64 overflow-y-auto pr-1 mb-4 divide-y divide-gray-100">
                {items.map((item) => (
                  <div key={item.cartItemId} className="pt-3 first:pt-0 flex items-center gap-3">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-14 h-14 rounded-lg object-cover border border-gray-200 bg-green-50 flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold text-gray-900 truncate">{item.name}</p>
                      {item.variant && <p className="text-[11px] text-gray-500">{item.variant}</p>}
                      <p className="text-xs text-gray-500">Qty: {item.quantity}</p>
                    </div>
                    <div className="text-xs font-bold text-gray-900">
                      {formatPrice(item.price * item.quantity)}
                    </div>
                  </div>
                ))}
              </div>

              {/* Promo Code Box */}
              <div className="bg-green-50/60 border border-green-200 rounded-xl p-3.5 mb-4">
                <label className="block text-xs font-bold text-green-900 mb-1.5">
                  Promo or Free Shipping Code
                </label>
                <form onSubmit={handlePromoSubmit} className="flex gap-2">
                  <input
                    type="text"
                    value={promoCodeInput}
                    onChange={(e) => setPromoCodeInput(e.target.value.toUpperCase())}
                    placeholder="Enter code (e.g. FREESHIP)"
                    data-testid="checkout-promo-input"
                    className="flex-1 px-3 py-2 rounded-lg border border-gray-300 bg-white text-xs font-mono uppercase focus:outline-none focus:ring-2 focus:ring-green-600"
                  />
                  {appliedPromoRule && (!promoCodeInput || promoCodeInput === appliedPromoRule.code) ? (
                    <button
                      type="button"
                      onClick={removePromoCode}
                      data-testid="checkout-remove-promo-btn"
                      className="px-3.5 py-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 text-xs font-bold cursor-pointer"
                    >
                      Remove
                    </button>
                  ) : (
                    <button
                      type="submit"
                      data-testid="checkout-apply-promo-btn"
                      className="px-4 py-2 rounded-lg bg-green-700 hover:bg-green-800 text-white text-xs font-bold cursor-pointer"
                    >
                      Apply
                    </button>
                  )}
                </form>
                {(promoFeedback?.message || promoFeedback?.text) && (
                  <p
                    className={`mt-1.5 text-xs font-semibold ${
                      promoFeedback.type === 'error' ? 'text-red-600' : 'text-green-800'
                    }`}
                    data-testid="checkout-promo-feedback"
                  >
                    {promoFeedback.message || promoFeedback.text}
                  </p>
                )}
              </div>

              {/* Financial Breakdown */}
              <div className="border-t border-gray-200 pt-4 space-y-2 text-sm">
                <div className="flex justify-between text-gray-600">
                  <span>Subtotal</span>
                  <span data-testid="checkout-subtotal">{formatPrice(subtotal)}</span>
                </div>
                {discountAmount > 0 && (
                  <div className="flex justify-between text-emerald-700 font-semibold" data-testid="checkout-discount-row">
                    <span className="truncate pr-2">
                      Discount {discountSummary ? `(${discountSummary})` : ''}
                    </span>
                    <span data-testid="checkout-discount">-{formatPrice(discountAmount)}</span>
                  </div>
                )}
                <div className="flex justify-between text-gray-600">
                  <span>
                    {requiresShipping ? 'Standard Shipping' : 'Digital Delivery'}
                    {freeShippingUnlocked && freeShippingReason ? (
                      <span className="ml-1 text-[11px] text-teal-700 font-semibold">({freeShippingReason})</span>
                    ) : null}
                  </span>
                  <span data-testid="checkout-shipping">
                    {shippingAmount === 0 ? 'FREE' : formatPrice(shippingAmount)}
                  </span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>
                    {activeTaxZone?.tax_name || 'HST'} ({activeTaxZone?.tax_rate ?? 13}%)
                  </span>
                  <span data-testid="checkout-tax">{formatPrice(taxAmount)}</span>
                </div>
                <div className="flex justify-between text-lg font-extrabold text-green-900 pt-2 border-t border-gray-200">
                  <span>Total ({currency})</span>
                  <span data-testid="checkout-total">{formatPrice(totalAmount)}</span>
                </div>
              </div>

              {/* PayPal Payment Action Box */}
              <div className="mt-6 pt-5 border-t border-gray-200">
                <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 text-center">
                  Express Payment Processing
                </div>

                <button
                  type="button"
                  disabled={isProcessing}
                  onClick={handleInitiatePayPalCheckout}
                  data-testid="paypal-checkout-button"
                  className="w-full bg-[#FFC439] hover:bg-[#f4b829] text-[#003087] font-extrabold py-3.5 px-6 rounded-xl shadow-md transition flex items-center justify-center gap-2 text-base cursor-pointer disabled:opacity-60"
                >
                  <span className="italic font-black text-lg tracking-tight">
                    <span className="text-[#003087]">Pay</span>
                    <span className="text-[#0079C1]">Pal</span>
                  </span>
                  <span className="text-sm font-bold text-gray-900">
                    {isProcessing ? 'Preparing Order...' : `Checkout (${formatPrice(totalAmount)})`}
                  </span>
                </button>

                <p className="text-[11px] text-gray-500 text-center mt-2.5">
                  🔒 256-bit SSL Encrypted • Pay with your PayPal balance, bank, or credit/debit card.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Interactive PayPal Order Approval Modal */}
      {pendingPayPalOrder && (
        <div
          className="fixed inset-0 z-50 bg-gray-900/70 backdrop-blur-xs flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="paypal-modal-title"
          data-testid="paypal-approval-modal"
        >
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl border border-gray-200 overflow-hidden">
            <div className="bg-[#003087] text-white px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="italic font-black text-xl tracking-tight">
                  <span className="text-white">Pay</span>
                  <span className="text-[#0079C1] bg-white px-1.5 py-0.5 rounded ml-0.5">Pal</span>
                </span>
                <span id="paypal-modal-title" className="text-xs font-semibold text-blue-100 ml-1">
                  Express Checkout
                </span>
              </div>
              <button
                type="button"
                onClick={() => setPendingPayPalOrder(null)}
                className="text-white/80 hover:text-white text-sm cursor-pointer"
                aria-label="Cancel PayPal payment"
              >
                ✕
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-blue-50/70 border border-blue-200 rounded-xl p-4 text-sm">
                <div className="flex justify-between text-xs text-gray-500 mb-1">
                  <span>Merchant</span>
                  <span>Order Ref</span>
                </div>
                <div className="flex justify-between font-bold text-gray-900">
                  <span>{settings?.paypal_merchant_name || 'Best 3 in Tennis Pro Shop'}</span>
                  <span data-testid="paypal-modal-order-ref">{pendingPayPalOrder.order_number}</span>
                </div>
                <div className="mt-3 pt-3 border-t border-blue-200/70 flex justify-between items-baseline">
                  <span className="text-xs font-semibold text-gray-600">
                    Includes {pendingPayPalOrder.breakdown.tax_name} ({pendingPayPalOrder.breakdown.tax_rate}%)
                  </span>
                  <span className="text-lg font-extrabold text-[#003087]" data-testid="paypal-modal-total">
                    ${Number(pendingPayPalOrder.breakdown.total).toFixed(2)}{' '}
                    {pendingPayPalOrder.breakdown.currency || 'CAD'}
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">PayPal Payer Email</label>
                <input
                  type="email"
                  value={paypalPayerEmail}
                  onChange={(e) => setPaypalPayerEmail(e.target.value)}
                  data-testid="paypal-payer-email-input"
                  className="w-full px-3.5 py-2 rounded-xl border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
                />
              </div>

              <div className="flex flex-col gap-2.5 pt-2">
                <button
                  type="button"
                  disabled={isProcessing}
                  onClick={handleConfirmPayPalCapture}
                  data-testid="paypal-approve-pay-btn"
                  className="w-full bg-[#0070BA] hover:bg-[#005ea6] text-white font-extrabold py-3 px-5 rounded-xl shadow-md transition text-sm cursor-pointer disabled:opacity-60"
                >
                  {isProcessing
                    ? 'Capturing Payment...'
                    : `Approve & Pay $${Number(pendingPayPalOrder.breakdown.total).toFixed(2)} ${
                        pendingPayPalOrder.breakdown.currency || 'CAD'
                      }`}
                </button>
                <button
                  type="button"
                  disabled={isProcessing}
                  onClick={() => setPendingPayPalOrder(null)}
                  className="w-full py-2.5 px-4 rounded-xl border border-gray-300 text-gray-700 font-semibold text-xs hover:bg-gray-50 cursor-pointer"
                >
                  Cancel &amp; Return to Checkout
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CheckoutPage;
